export type TemplateTokenValues = Record<string, string | number>;

const TOKEN_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;

function toTitleCase(value: string): string {
  return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Applies the casing of the token itself to its value, so a single token can be
 * referenced in upper, title, or lower case:
 *   {{MONTH}} -> "MARCH 2026", {{Month}} -> "March 2026", {{month}} -> "march 2026"
 * Tokens without letters (e.g. purely numeric values) are returned unchanged.
 */
function applyTokenCasing(token: string, value: string): string {
  if (!/[a-z]/i.test(token)) {
    return value;
  }
  if (token === token.toUpperCase()) {
    return value.toUpperCase();
  }
  if (token === token.toLowerCase()) {
    return value.toLowerCase();
  }
  return toTitleCase(value);
}

/**
 * Replaces `{{token}}` placeholders in a template, matching tokens case-insensitively
 * and casing the substituted value to match the token (see `applyTokenCasing`).
 * Unknown tokens are left untouched.
 */
export function replaceTemplateTokens(template: string, values: TemplateTokenValues): string {
  return template.replace(TOKEN_PATTERN, (match, rawToken: string) => {
    const key = rawToken.toLowerCase();
    if (!(key in values)) {
      return match;
    }
    return applyTokenCasing(rawToken, String(values[key]));
  });
}
